/**
 * AURA Color Effects
 * CSS injection for contrast, saturation, monochrome, and custom color overrides
 */

import { ColorSettings } from '../../core/types';
import { cssInjector } from '../../core/css-injector';

const STYLE_IDS = {
  darkContrast: 'dark-contrast',
  lightContrast: 'light-contrast',
  highContrast: 'high-contrast',
  highSaturation: 'high-saturation',
  lowSaturation: 'low-saturation',
  monochrome: 'monochrome',
  textColor: 'text-color',
  titleColor: 'title-color',
  backgroundColor: 'background-color',
};

export function applyColorStyles(settings: ColorSettings): void {
  // Dark Contrast – explicit dark overrides instead of filter:invert so
  // pages that are already dark don't get flipped back to light.
  if (settings.darkContrast) {
    cssInjector.setStylesheet(
      STYLE_IDS.darkContrast,
      `
        :where(html) body {
          background-color: #0d0d0d !important;
          color: #e0e0e0 !important;
        }
        :where(html) body * {
          background-color: #0d0d0d !important;
          color: #e0e0e0 !important;
          border-color: #444444 !important;
          text-shadow: none !important;
        }
        :where(html) body a { color: #82b1ff !important; }
        :where(html) body button, :where(html) body [role="button"],
        :where(html) body input[type="submit"],
        :where(html) body input[type="button"] {
          border: 1px solid #666666 !important;
        }
        :where(html) body img, :where(html) body video,
        :where(html) body picture, :where(html) body canvas,
        :where(html) body svg:not([data-aura-shield]) {
          opacity: 1 !important;
        }
      `,
      100
    );
  } else {
    cssInjector.removeStylesheet(STYLE_IDS.darkContrast);
  }

  // Light Contrast – explicit white backgrounds on all elements instead of
  // transparent, so pages already in light mode keep visual structure.
  if (settings.lightContrast) {
    cssInjector.setStylesheet(
      STYLE_IDS.lightContrast,
      `
        :where(html) body {
          background-color: #ffffff !important;
          color: #000000 !important;
        }
        :where(html) body * {
          background-color: #ffffff !important;
          color: #000000 !important;
          border-color: #333333 !important;
          text-shadow: none !important;
        }
        :where(html) body a {
          color: #0000cc !important;
          text-decoration: underline !important;
        }
        :where(html) body button, :where(html) body [role="button"],
        :where(html) body input[type="submit"],
        :where(html) body input[type="button"] {
          border: 1px solid #333333 !important;
        }
        :where(html) body img, :where(html) body video,
        :where(html) body canvas { opacity: 1 !important; }
      `,
      100
    );
  } else {
    cssInjector.removeStylesheet(STYLE_IDS.lightContrast);
  }

  // High Contrast
  if (settings.highContrast) {
    cssInjector.setStylesheet(
      STYLE_IDS.highContrast,
      `
        :where(html) body { background-color: #000000 !important; }
        :where(html) body * {
          background-color: #000000 !important;
          color: #ffff00 !important;
          border-color: #ffff00 !important;
        }
        :where(html) body a { color: #00ffff !important; }
        :where(html) body button, :where(html) body [role="button"],
        :where(html) body input[type="submit"],
        :where(html) body input[type="button"] {
          border: 2px solid #ffff00 !important;
        }
        :where(html) body img, :where(html) body video,
        :where(html) body canvas {
          opacity: 1 !important; filter: none !important;
        }
      `,
      100
    );
  } else {
    cssInjector.removeStylesheet(STYLE_IDS.highContrast);
  }

  // High Saturation
  if (settings.highSaturation) {
    cssInjector.setStylesheet(
      STYLE_IDS.highSaturation,
      `:where(html) body { filter: saturate(1.5) !important; }`,
      50
    );
  } else {
    cssInjector.removeStylesheet(STYLE_IDS.highSaturation);
  }

  // Low Saturation
  if (settings.lowSaturation) {
    cssInjector.setStylesheet(
      STYLE_IDS.lowSaturation,
      `:where(html) body { filter: saturate(0.5) !important; }`,
      50
    );
  } else {
    cssInjector.removeStylesheet(STYLE_IDS.lowSaturation);
  }

  // Monochrome
  if (settings.monochrome) {
    cssInjector.setStylesheet(
      STYLE_IDS.monochrome,
      `:where(html) body { filter: grayscale(100%) !important; }`,
      51
    );
  } else {
    cssInjector.removeStylesheet(STYLE_IDS.monochrome);
  }

  // Custom Text Color
  if (settings.textColor) {
    cssInjector.setStylesheet(
      STYLE_IDS.textColor,
      `
        :where(html) body, :where(html) body p, :where(html) body span,
        :where(html) body li, :where(html) body td, :where(html) body th,
        :where(html) body label {
          color: ${settings.textColor} !important;
        }
      `,
      60
    );
  } else {
    cssInjector.removeStylesheet(STYLE_IDS.textColor);
  }

  // Custom Title Color
  if (settings.titleColor) {
    cssInjector.setStylesheet(
      STYLE_IDS.titleColor,
      `
        :where(html) body h1, :where(html) body h2, :where(html) body h3,
        :where(html) body h4, :where(html) body h5, :where(html) body h6,
        :where(html) body [role="heading"] {
          color: ${settings.titleColor} !important;
        }
      `,
      61
    );
  } else {
    cssInjector.removeStylesheet(STYLE_IDS.titleColor);
  }

  // Custom Background Color
  if (settings.backgroundColor) {
    cssInjector.setStylesheet(
      STYLE_IDS.backgroundColor,
      `
        :where(html) body {
          background-color: ${settings.backgroundColor} !important;
        }
      `,
      59
    );
  } else {
    cssInjector.removeStylesheet(STYLE_IDS.backgroundColor);
  }
}
